import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { BRAND } from '@fin/shared/brand';
import { SERVICE_TYPES, SERVICE_LABELS, STATUS_LABELS, EMPLOYMENT_TYPES } from '@fin/shared/enums';
import { buildWhatsAppLink } from '@fin/shared/whatsapp';

import { Button, Card, Field, Modal, PageHeader, inputClass, useDisclosure } from '../components/ui';
import {
  useDeleteWorker,
  useJobsByWorker,
  usePatchWorker,
  useWorker,
  type Job,
  type Worker,
} from '../lib/queries';

const TZ = 'Asia/Dubai';
function fmt(s?: string) {
  if (!s) return '—';
  try {
    return new Intl.DateTimeFormat('en-AE', {
      timeZone: TZ,
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(s));
  } catch {
    return s;
  }
}

export default function WorkerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const worker = useWorker(id);
  const jobs = useJobsByWorker(id);
  const patch = usePatchWorker();
  const del = useDeleteWorker();
  const editModal = useDisclosure();
  const deleteModal = useDisclosure();

  if (worker.isPending) return <p className="text-stone-500">Loading…</p>;
  if (worker.isError)
    return <p style={{ color: BRAND.danger }}>Failed to load: {(worker.error as Error).message}</p>;
  if (!worker.data) return null;
  const w = worker.data;

  const sortedJobs = [...(jobs.data ?? [])].sort((a, b) =>
    a.scheduledAt < b.scheduledAt ? 1 : -1,
  );

  return (
    <>
      <PageHeader
        title={w.name}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/workers" className="text-sm text-stone-500 hover:underline">
              ← All workers
            </Link>
            <Button onClick={editModal.onOpen} variant="secondary">
              Edit
            </Button>
            <Button
              onClick={() => {
                const tempPassword = window.prompt(
                  `Send temp credentials to ${w.name} via WhatsApp.\n\nEnter the temporary password:`,
                  '',
                );
                if (!tempPassword) return;
                const url = buildWhatsAppLink(w.phone, 'worker_temp_credentials', {
                  username: w.email ?? w.phone,
                  tempPassword,
                  loginUrl: 'https://app.fixitninjaz.com',
                });
                window.open(url, '_blank', 'noopener,noreferrer');
              }}
              variant="secondary"
            >
              Send temp creds
            </Button>
            <Button
              variant={w.active ? 'danger' : 'primary'}
              onClick={() => patch.mutate({ id: w.id, patch: { active: !w.active } })}
              disabled={patch.isPending}
            >
              {w.active ? 'Deactivate' : 'Activate'}
            </Button>
            <Button variant="danger" onClick={deleteModal.onOpen} disabled={del.isPending}>
              Delete
            </Button>
          </div>
        }
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <div className="border-b border-stone-200 px-4 py-3 text-xs uppercase tracking-wide text-stone-500">
            Profile
          </div>
          <div className="divide-y divide-stone-100">
            <Row label="Phone">{w.phone}</Row>
            <Row label="Email">{w.email ?? '—'}</Row>
            <Row label="Type">{w.employmentType.replace('_', ' ')}</Row>
            <Row label="Home area">{w.homeArea ?? '—'}</Row>
            <Row label="Status">
              <span
                className="rounded-full px-2 py-0.5 text-xs"
                style={{
                  backgroundColor: w.active ? '#DCFCE7' : '#FEE2E2',
                  color: w.active ? BRAND.success : BRAND.danger,
                }}
              >
                {w.active ? 'active' : 'inactive'}
              </span>
            </Row>
            <Row label="Cognito sub">
              <span className="font-mono text-xs">{w.cognitoSub ?? 'not yet linked'}</span>
            </Row>
            <Row label="Created">{fmt(w.createdAt)}</Row>
          </div>
        </Card>
        <Card>
          <div className="border-b border-stone-200 px-4 py-3 text-xs uppercase tracking-wide text-stone-500">
            Services
          </div>
          <div className="flex flex-wrap gap-2 p-4">
            {w.services.length === 0 ? (
              <span className="text-sm text-stone-500">No services configured.</span>
            ) : (
              w.services.map((s) => (
                <span
                  key={s}
                  className="rounded-full bg-stone-100 px-3 py-1 text-xs text-stone-700"
                >
                  {SERVICE_LABELS[s as keyof typeof SERVICE_LABELS]?.en ?? s}
                </span>
              ))
            )}
          </div>
        </Card>
      </div>

      <div className="mt-6">
        <Card>
          <div className="flex items-center justify-between border-b border-stone-200 px-4 py-3">
            <h3 className="text-xs uppercase tracking-wide text-stone-500">
              Jobs ({sortedJobs.length})
            </h3>
            {jobs.isPending && <span className="text-xs text-stone-400">Loading…</span>}
          </div>
          {sortedJobs.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-stone-500">
              No jobs assigned to this worker yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="border-b border-stone-200 text-left text-xs uppercase tracking-wide text-stone-500">
                <tr>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Service</th>
                  <th className="px-4 py-3">Priority</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Open</th>
                </tr>
              </thead>
              <tbody>
                {sortedJobs.map((j: Job) => (
                  <tr key={j.id} className="border-b border-stone-100 last:border-0">
                    <td className="px-4 py-3 text-stone-700">{fmt(j.scheduledAt)}</td>
                    <td className="px-4 py-3 text-stone-600">
                      {SERVICE_LABELS[j.serviceType as keyof typeof SERVICE_LABELS]?.en ??
                        j.serviceType}
                    </td>
                    <td className="px-4 py-3 text-stone-600">{j.priority}</td>
                    <td className="px-4 py-3 text-stone-600">
                      {STATUS_LABELS[j.status]?.en ?? j.status}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/jobs/${j.id}`}
                        className="text-xs hover:underline"
                        style={{ color: BRAND.primary }}
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </Card>
      </div>

      <Modal open={editModal.open} onClose={editModal.onClose} title={`Edit ${w.name}`}>
        <EditWorkerForm
          worker={w}
          submitting={patch.isPending}
          onCancel={editModal.onClose}
          onSave={async (body) => {
            await patch.mutateAsync({ id: w.id, patch: body });
            editModal.onClose();
          }}
        />
      </Modal>
      <Modal open={deleteModal.open} onClose={deleteModal.onClose} title={`Delete ${w.name}`}>
        <DeleteWorkerForm
          worker={w}
          submitting={del.isPending}
          onCancel={deleteModal.onClose}
          onConfirm={async () => {
            try {
              const res = await del.mutateAsync(w.id);
              deleteModal.onClose();
              alert(
                `${w.name} deleted.${res.unassignedJobs > 0 ? ` ${res.unassignedJobs} open job(s) unassigned.` : ''}`,
              );
              navigate('/workers');
            } catch (err) {
              alert(`Failed: ${err instanceof Error ? err.message : String(err)}`);
            }
          }}
        />
      </Modal>
    </>
  );
}

function DeleteWorkerForm({
  worker,
  submitting,
  onCancel,
  onConfirm,
}: {
  worker: Worker;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [confirmName, setConfirmName] = useState('');
  const matches = confirmName.trim().toLowerCase() === worker.name.trim().toLowerCase();
  return (
    <div className="flex flex-col gap-4">
      <div
        role="alert"
        className="rounded border px-3 py-3 text-sm"
        style={{ borderColor: BRAND.danger, color: BRAND.danger, backgroundColor: '#FEF2F2' }}
      >
        <p className="font-semibold">This will permanently delete the worker.</p>
        <ul className="mt-2 list-disc pl-5 text-xs">
          <li>The Cognito sign-in account is removed (cannot log in again).</li>
          <li>Any open jobs assigned to this worker are returned to the schedule pool.</li>
          <li>Completed and cancelled jobs keep their history intact.</li>
        </ul>
      </div>
      <Field
        label={`Type the worker's name (${worker.name}) to confirm.`}
        hint="This is case-insensitive."
      >
        <input
          className={inputClass}
          value={confirmName}
          onChange={(e) => setConfirmName(e.target.value)}
          autoFocus
        />
      </Field>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={onConfirm}
          disabled={!matches || submitting}
        >
          {submitting ? 'Deleting…' : 'Permanently delete'}
        </Button>
      </div>
    </div>
  );
}

function EditWorkerForm({
  worker,
  submitting,
  onCancel,
  onSave,
}: {
  worker: Worker;
  submitting: boolean;
  onCancel: () => void;
  onSave: (body: Partial<Worker>) => Promise<void>;
}) {
  const [name, setName] = useState(worker.name);
  const [phone, setPhone] = useState(worker.phone);
  const [email, setEmail] = useState(worker.email ?? '');
  const [employmentType, setEmploymentType] = useState<Worker['employmentType']>(worker.employmentType);
  const [services, setServices] = useState<string[]>([...worker.services]);
  const [homeArea, setHomeArea] = useState(worker.homeArea ?? '');
  const [error, setError] = useState<string | null>(null);

  function toggleService(s: string) {
    setServices((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (services.length === 0) {
      setError('Select at least one service.');
      return;
    }
    try {
      const body: Partial<Worker> = {
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
        employmentType,
        services,
        homeArea: homeArea.trim() || undefined,
      };
      await onSave(body);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      {error && (
        <div
          role="alert"
          className="rounded border px-3 py-2 text-xs"
          style={{ borderColor: BRAND.danger, color: BRAND.danger, backgroundColor: '#FEF2F2' }}
        >
          {error}
        </div>
      )}
      <Field label="Name">
        <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} required />
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Phone (UAE +971…)">
          <input className={inputClass} value={phone} onChange={(e) => setPhone(e.target.value)} required />
        </Field>
        <Field label="Email" hint="Used for sign-in.">
          <input
            className={inputClass}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Employment type">
          <select
            className={inputClass}
            value={employmentType}
            onChange={(e) => setEmploymentType(e.target.value as Worker['employmentType'])}
          >
            {EMPLOYMENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t.replace('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Home area (optional)">
          <input className={inputClass} value={homeArea} onChange={(e) => setHomeArea(e.target.value)} />
        </Field>
      </div>
      <Field label="Services" hint="Pick at least one.">
        <div className="flex flex-wrap gap-2">
          {SERVICE_TYPES.map((s) => {
            const on = services.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleService(s)}
                className="rounded-full border px-3 py-1 text-xs"
                style={{
                  borderColor: on ? BRAND.primary : BRAND.border,
                  backgroundColor: on ? BRAND.primary : 'white',
                  color: on ? 'white' : BRAND.text,
                }}
              >
                {SERVICE_LABELS[s].en}
              </button>
            );
          })}
        </div>
      </Field>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </form>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-3 px-4 py-3 text-sm">
      <div className="text-stone-500">{label}</div>
      <div className="col-span-2 text-stone-800">{children}</div>
    </div>
  );
}
